import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { toaster } from '@/components/ui/toaster'
import { WBDApi } from '@/open-api'
import type {
  CreateGraphicDto,
  GetGraphicsResponseDto,
  GraphicItemDto
} from '@/open-api/Api'

export type Graphic = GraphicItemDto
export type GraphicsResponse = GetGraphicsResponseDto

export const GRAPHICS_QUERY_KEY = 'graphics'
const GRAPHICS_PER_PAGE = 24

export const useGraphics = (collectionId?: string, skip = 0) => {
  return useQuery({
    queryKey: [GRAPHICS_QUERY_KEY, { collectionId, skip }],
    queryFn: () =>
      WBDApi.graphics
        .graphicControllerGetGraphics({
          collectionIds: collectionId ? [collectionId] : undefined,
          take: GRAPHICS_PER_PAGE,
          skip
        })
        .then(({ data }) => data)
  })
}

export const useCreateGraphic = () => {
  return useMutation({
    mutationFn: (payload: CreateGraphicDto) =>
      WBDApi.graphics
        .graphicControllerCreateGraphic(payload)
        .then(({ data }) => data),
    onError: (_, variables) => {
      toaster.create({
        title: 'Error',
        description: `Failed to create graphic: ${variables.name}`,
        type: 'error'
      })
    }
  })
}

export const useDeleteGraphic = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      WBDApi.graphics.graphicControllerDeleteGraphic(id),
    onSuccess: () => {
      toaster.create({
        title: 'Graphic deleted',
        type: 'success'
      })
      queryClient.invalidateQueries({ queryKey: [GRAPHICS_QUERY_KEY] })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to delete graphic',
        type: 'error'
      })
    }
  })
}

export const useBulkDeleteGraphics = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) => WBDApi.graphics.graphicControllerDeleteGraphic(id))
      )
    },
    onSuccess: () => {
      toaster.create({
        title: 'Graphics deleted',
        type: 'success'
      })
      queryClient.invalidateQueries({ queryKey: [GRAPHICS_QUERY_KEY] })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to delete graphics',
        type: 'error'
      })
    }
  })
}

export const useGraphicsByIds = (ids: string[]) => {
  return useQuery({
    queryKey: [GRAPHICS_QUERY_KEY, 'byIds', ids],
    queryFn: () =>
      WBDApi.graphics
        .graphicControllerGetGraphics({ take: 1000, skip: 0 })
        .then(({ data }) => {
          const idSet = new Set(ids)
          return data.items.filter((item) => idSet.has(item.id))
        }),
    enabled: ids.length > 0
  })
}

export const useInfiniteGraphics = (collectionId?: string) => {
  return useInfiniteQuery({
    queryKey: [GRAPHICS_QUERY_KEY, 'infinite', { collectionId }],
    queryFn: ({ pageParam }) =>
      WBDApi.graphics
        .graphicControllerGetGraphics({
          collectionIds: collectionId ? [collectionId] : undefined,
          take: GRAPHICS_PER_PAGE,
          skip: pageParam
        })
        .then(({ data }) => data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.items.length,
        0
      )
      if (totalFetched >= lastPage.count) {
        return undefined
      }
      return totalFetched
    }
  })
}
